import {
  useOrganisationList,
  useSite,
  useSiteList,
  useSites,
} from "@inrange/building-manager-api-client";
import { sortBy } from "@inrange/utils/utils.ts";
import { useState } from "react";
import { Button } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { csvHeaders, formatCsvRow } from "../../utils/SitesCsvUtil";
import Loading from "../Loading";
import CollapsibleCard from "../site/CollapsibleCard";
import { isLinkedToNonTestOrg } from "../site/siteSections/utils";

const SiteDataExport = () => {
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const { fetchOrganisations } = useOrganisationList();
  const { fetchSites } = useSiteList();
  const sites = useSites((fetchSites.data?.sites || []).map((site) => site.id));

  if (fetchOrganisations.isError || fetchSites.isError) {
    return <div>Error loading site list</div>;
  }

  if (fetchOrganisations.isLoading || fetchSites.isLoading) {
    return <Loading height="100px" label="Loading site list ..." />;
  }

  if (fetchSiteValues.isError) {
    return <div>Error loading site values.</div>;
  }

  if (fetchSiteValues.isLoading) {
    return <Loading label="Loading site values data..." />;
  }

  const anySitesLoading = sites.some(
    (site: { isLoading: boolean }) => site.isLoading
  );
  if (anySitesLoading) {
    return <Loading height="100px" label={`Loading site data ...`} />;
  }

  const errorSites = sites.filter((site: { isError: boolean }) => site.isError);
  if (errorSites.length > 0) {
    return (
      <div>
        {`Failed to load ${errorSites.length} of ${sites.length} sites`}
      </div>
    );
  }

  const successfulSites = sites
    .filter((site: { isSuccess: boolean }) => site.isSuccess)
    .filter((site) => !!site.data?.sites)
    .flatMap((site) => site.data!.sites!)
    .sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

  const csvData = [...successfulSites].sort(sortBy((s) => s.id)).map((site) => {
    return formatCsvRow(
      site,
      "all-sites",
      isLinkedToNonTestOrg(site) ? "production" : "test",
      fetchSiteValues.data!.options.pvInverters
    );
  });
  const dateString = new Date().toISOString().split(".")[0];

  return (
    <CSVLink
      data={csvData}
      headers={csvHeaders}
      filename={"Site List " + dateString + ".csv"}
    >
      <Button variant="primary" size="sm">
        Download sites data CSV
      </Button>
    </CSVLink>
  );
};

const ExportAllSitesCard = () => {
  const [loadSiteData, setLoadSiteData] = useState(false);

  return (
    <CollapsibleCard title="Export All Sites" initialOpen={false}>
      {!loadSiteData && (
        <Button variant="success" size="sm" onClick={() => setLoadSiteData(true)}>
          Load All Sites Data
        </Button>
      )}
      {loadSiteData && <SiteDataExport />}
    </CollapsibleCard>
  );
};

export default ExportAllSitesCard;
